import { z } from "zod";

export const SYNTH_DOCUMENT_FORMAT = "patchwork-synth";
export const CURRENT_SYNTH_DOCUMENT_VERSION = 1;

const finiteNumberSchema = z.number().finite();
const positiveNumberSchema = finiteNumberSchema.refine((value) => value > 0, {
  message: "Expected a positive number",
});

export const pointSchema = z
  .object({
    x: finiteNumberSchema,
    y: finiteNumberSchema,
  })
  .strict();

export const frameSchema = z
  .object({
    x: finiteNumberSchema,
    y: finiteNumberSchema,
    width: positiveNumberSchema,
    height: positiveNumberSchema,
  })
  .strict();

export const normalizedAnchorSchema = z
  .object({
    x: finiteNumberSchema.min(0).max(1),
    y: finiteNumberSchema.min(0).max(1),
  })
  .strict();

export type JsonPrimitive = boolean | null | number | string;
export type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };
export type JsonObject = { [key: string]: JsonValue };

export const jsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    finiteNumberSchema,
    z.boolean(),
    z.null(),
    z.array(jsonValueSchema),
    z.record(z.string(), jsonValueSchema),
  ]),
);

export const artboardSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    kind: z.enum(["front", "rear", "keyboard", "side", "expansion", "custom"]).default("custom"),
    frame: frameSchema,
    backgroundStyleRef: z.string().min(1).optional(),
    visible: z.boolean().default(true),
    locked: z.boolean().default(false),
  })
  .strict();

export const componentReferenceSchema = z
  .object({
    type: z.string().min(1),
    variant: z.string().min(1).optional(),
    props: z.record(z.string(), jsonValueSchema).default({}),
  })
  .strict();

export const textContentSchema = z
  .object({
    value: z.string(),
    align: z.enum(["left", "center", "right"]).default("center"),
    verticalAlign: z.enum(["top", "middle", "bottom", "baseline"]).default("top"),
    wrap: z.boolean().default(false),
    frameMode: z.enum(["hug", "fixed-width", "fixed-box"]),
    overflow: z.enum(["visible", "clip", "shrink"]),
  })
  .strict();

export const shapeContentSchema = z.discriminatedUnion("type", [
  z
    .object({
      type: z.literal("rect"),
      radius: finiteNumberSchema.nonnegative().optional(),
    })
    .strict(),
  z.object({ type: z.literal("ellipse") }).strict(),
  z
    .object({
      type: z.literal("line"),
      from: pointSchema,
      to: pointSchema,
    })
    .strict(),
  z
    .object({
      type: z.literal("polyline"),
      points: z.array(pointSchema).min(2),
      closed: z.boolean().default(false),
    })
    .strict(),
  z
    .object({
      type: z.literal("path"),
      data: z.string().min(1),
    })
    .strict(),
]);

export const imageContentSchema = z
  .object({
    assetId: z.string().min(1),
    fit: z.enum(["contain", "cover", "fill", "none"]).default("contain"),
  })
  .strict();

export const symbolContentSchema = z.discriminatedUnion("type", [
  z
    .object({
      type: z.literal("waveform"),
      waveform: z.enum(["sine", "saw", "ramp", "square", "triangle", "narrow-pulse"]),
    })
    .strict(),
  z
    .object({
      type: z.literal("signal-flow"),
      from: z.string().min(1),
      to: z.string().min(1),
    })
    .strict(),
]);

export const semanticBindingSchema = z
  .object({
    kind: z.enum(["control", "port", "signal-node"]),
    entityId: z.string().min(1),
    anchor: z.string().min(1).optional(),
  })
  .strict();

export const accessibilitySchema = z
  .object({
    name: z.string().min(1).optional(),
    description: z.string().min(1).optional(),
    keyboardOrder: z.number().int().nonnegative().optional(),
  })
  .strict();

export const sceneNodeSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    kind: z.enum(["surface", "group", "component", "text", "shape", "image", "symbol"]),
    frame: frameSchema,
    rotation: finiteNumberSchema.default(0),
    anchor: normalizedAnchorSchema.default({ x: 0.5, y: 0.5 }),
    children: z.array(z.string().min(1)).default([]),
    artboardId: z.string().min(1).optional(),
    visible: z.boolean().default(true),
    locked: z.boolean().default(false),
    selectionRole: z.enum(["composite", "slot", "atomic"]).default("composite"),
    hitTest: z.enum(["normal", "pass-through"]).optional(),
    component: componentReferenceSchema.optional(),
    text: textContentSchema.optional(),
    shape: shapeContentSchema.optional(),
    image: imageContentSchema.optional(),
    symbol: symbolContentSchema.optional(),
    binding: semanticBindingSchema.optional(),
    styleRef: z.string().min(1).optional(),
    styleOverrides: z.record(z.string(), jsonValueSchema).optional(),
    accessibility: accessibilitySchema.optional(),
  })
  .strict()
  .superRefine((node, context) => {
    const requiredContent = {
      component: node.component,
      image: node.image,
      shape: node.shape,
      symbol: node.symbol,
      text: node.text,
    } as const;

    for (const [kind, content] of Object.entries(requiredContent)) {
      if (node.kind === kind && content === undefined) {
        context.addIssue({
          code: "custom",
          path: [kind],
          message: `Node kind '${kind}' requires ${kind} content`,
        });
      }
      if (node.kind !== kind && content !== undefined) {
        context.addIssue({
          code: "custom",
          path: [kind],
          message: `Only a '${kind}' node may define ${kind} content`,
        });
      }
    }
  });

export const controlOptionSchema = z
  .object({
    id: z.string().min(1),
    label: z.string().min(1),
    value: finiteNumberSchema,
  })
  .strict();

export const controlEntitySchema = z
  .object({
    id: z.string().min(1),
    label: z.string().min(1),
    shortLabel: z.string().min(1).optional(),
    type: z.enum(["continuous", "discrete", "momentary", "toggle"]),
    defaultValue: finiteNumberSchema,
    minimum: finiteNumberSchema.default(0),
    maximum: finiteNumberSchema.default(1),
    step: positiveNumberSchema.optional(),
    unit: z.string().min(1).optional(),
    options: z.array(controlOptionSchema).optional(),
  })
  .strict()
  .superRefine((control, context) => {
    if (control.maximum <= control.minimum) {
      context.addIssue({
        code: "custom",
        path: ["maximum"],
        message: "Control maximum must be greater than its minimum",
      });
    }
    if (control.defaultValue < control.minimum || control.defaultValue > control.maximum) {
      context.addIssue({
        code: "custom",
        path: ["defaultValue"],
        message: "Control default must be inside its range",
      });
    }
    if (control.type === "discrete" && (!control.options || control.options.length < 2)) {
      context.addIssue({
        code: "custom",
        path: ["options"],
        message: "Discrete controls require at least two options",
      });
    }
    if (control.type !== "discrete" && control.options !== undefined) {
      context.addIssue({
        code: "custom",
        path: ["options"],
        message: "Only discrete controls may define options",
      });
    }
    if (control.type === "discrete" && control.options) {
      const optionIds = new Set<string>();
      let defaultMatchesOption = false;
      control.options.forEach((option, index) => {
        if (optionIds.has(option.id)) {
          context.addIssue({
            code: "custom",
            path: ["options", index, "id"],
            message: "Discrete control option ids must be unique",
          });
        }
        optionIds.add(option.id);
        if (option.value < control.minimum || option.value > control.maximum) {
          context.addIssue({
            code: "custom",
            path: ["options", index, "value"],
            message: "Discrete control option values must be inside the control range",
          });
        }
        if (Math.abs(option.value - control.defaultValue) <= Number.EPSILON) {
          defaultMatchesOption = true;
        }
      });
      if (!defaultMatchesOption) {
        context.addIssue({
          code: "custom",
          path: ["defaultValue"],
          message: "Discrete control default must match one of its option values",
        });
      }
    }
  });

export const signalTypeSchema = z.enum([
  "audio",
  "cv",
  "gate",
  "clock",
  "expression",
  "midi",
  "power",
  "mixed",
  "unknown",
]);

export const portEntitySchema = z
  .object({
    id: z.string().min(1),
    label: z.string().min(1),
    shortLabel: z.string().min(1).optional(),
    direction: z.enum(["input", "output", "bidirectional"]),
    signalType: signalTypeSchema,
    connectorType: z
      .enum(["3.5mm-ts", "6.35mm-ts", "6.35mm-trs", "din-midi", "usb", "power", "other"])
      .default("3.5mm-ts"),
    description: z.string().min(1).optional(),
    electrical: z
      .object({
        impedance: z.string().min(1).optional(),
        range: z.string().min(1).optional(),
        coupling: z.string().min(1).optional(),
      })
      .strict()
      .optional(),
    normalledFrom: z.string().min(1).optional(),
  })
  .strict();

export const signalNodeEntitySchema = z
  .object({
    id: z.string().min(1),
    label: z.string().min(1),
    shortLabel: z.string().min(1).optional(),
    sectionId: z.string().min(1).optional(),
    position: pointSchema.optional(),
    signalType: signalTypeSchema,
    kind: z.enum(["source", "processor", "bus"]),
  })
  .strict();

export const signalConnectionSchema = z
  .object({
    id: z.string().min(1),
    label: z.string().min(1).optional(),
    description: z.string().min(1).optional(),
    fromId: z.string().min(1),
    toId: z.string().min(1),
    kind: z.enum(["internal", "normalled"]).default("internal"),
    signalType: signalTypeSchema,
    bidirectional: z.boolean().optional(),
    breakCondition: z
      .enum(["destination-patched", "source-patched", "either-patched", "never"])
      .default("never"),
    activeWhen: z
      .object({
        controlId: z.string().min(1),
        optionIds: z.array(z.string().min(1)).min(1),
      })
      .strict()
      .optional(),
  })
  .strict();

export const styleTokenSchema = z
  .object({
    value: jsonValueSchema,
    description: z.string().optional(),
  })
  .strict();

export const namedStyleSchema = z
  .object({
    extends: z.string().min(1).optional(),
    properties: z.record(z.string(), jsonValueSchema).default({}),
  })
  .strict();

export const assetReferenceSchema = z
  .object({
    id: z.string().min(1),
    kind: z.enum(["font", "image", "svg"]),
    contentHash: z.string().min(1),
    mimeType: z.string().min(1),
    source: z.string().min(1).optional(),
    metadata: z.record(z.string(), jsonValueSchema).default({}),
  })
  .strict();

export const editorGuideSchema = z
  .object({
    id: z.string().min(1),
    axis: z.enum(["x", "y"]),
    value: finiteNumberSchema,
    label: z.string().min(1).optional(),
    artboardId: z.string().min(1).optional(),
  })
  .strict();

export const editorMetadataSchema = z
  .object({
    guides: z.array(editorGuideSchema).default([]),
  })
  .strict();

export const synthDocumentSchema = z
  .object({
    format: z.literal(SYNTH_DOCUMENT_FORMAT),
    formatVersion: z.literal(CURRENT_SYNTH_DOCUMENT_VERSION),
    id: z.string().min(1),
    revision: z.number().int().positive(),
    metadata: z
      .object({
        manufacturer: z.string().min(1).optional(),
        model: z.string().min(1),
        displayName: z.string().min(1),
        description: z.string().optional(),
        createdAt: z.string().min(1).optional(),
        updatedAt: z.string().min(1).optional(),
      })
      .strict(),
    coordinateSystem: z
      .object({
        unit: z.literal("mm"),
        precision: z.number().int().min(0).max(6).default(2),
      })
      .strict(),
    artboards: z.record(z.string().min(1), artboardSchema),
    roots: z.array(z.string().min(1)).min(1),
    nodes: z.record(z.string().min(1), sceneNodeSchema),
    semantics: z
      .object({
        controls: z.record(z.string().min(1), controlEntitySchema).default({}),
        ports: z.record(z.string().min(1), portEntitySchema).default({}),
        signalNodes: z.record(z.string().min(1), signalNodeEntitySchema).default({}),
        connections: z.array(signalConnectionSchema).default([]),
      })
      .strict(),
    styles: z
      .object({
        tokens: z.record(z.string().min(1), styleTokenSchema).default({}),
        definitions: z.record(z.string().min(1), namedStyleSchema).default({}),
      })
      .strict(),
    assets: z.record(z.string().min(1), assetReferenceSchema).default({}),
    editor: editorMetadataSchema.default({ guides: [] }),
    componentLibrary: z
      .object({
        package: z.string().min(1),
        version: z.number().int().positive(),
      })
      .strict(),
  })
  .strict();

export type Point = z.infer<typeof pointSchema>;
export type Frame = z.infer<typeof frameSchema>;
export type NormalizedAnchor = z.infer<typeof normalizedAnchorSchema>;
export type Artboard = z.infer<typeof artboardSchema>;
export type SceneNode = z.infer<typeof sceneNodeSchema>;
export type ComponentReference = z.infer<typeof componentReferenceSchema>;
export type SemanticBinding = z.infer<typeof semanticBindingSchema>;
export type ControlEntity = z.infer<typeof controlEntitySchema>;
export type PortEntity = z.infer<typeof portEntitySchema>;
export type SignalNodeEntity = z.infer<typeof signalNodeEntitySchema>;
export type SignalConnection = z.infer<typeof signalConnectionSchema>;
export type NamedStyle = z.infer<typeof namedStyleSchema>;
export type AssetReference = z.infer<typeof assetReferenceSchema>;
export type EditorGuide = z.infer<typeof editorGuideSchema>;
export type EditorMetadata = z.infer<typeof editorMetadataSchema>;
export type SynthDocument = z.infer<typeof synthDocumentSchema>;

export function parseSynthDocument(value: unknown): SynthDocument {
  return synthDocumentSchema.parse(value);
}

export function cloneSynthDocument(document: SynthDocument): SynthDocument {
  return structuredClone(document);
}

export function createEmptySynthDocument(options: {
  id: string;
  model: string;
  displayName?: string;
  manufacturer?: string;
  width: number;
  height: number;
}): SynthDocument {
  const artboardId = "front";
  const rootId = "front-surface";
  return parseSynthDocument({
    format: SYNTH_DOCUMENT_FORMAT,
    formatVersion: CURRENT_SYNTH_DOCUMENT_VERSION,
    id: options.id,
    revision: 1,
    metadata: {
      manufacturer: options.manufacturer,
      model: options.model,
      displayName: options.displayName ?? options.model,
    },
    coordinateSystem: { unit: "mm", precision: 2 },
    artboards: {
      [artboardId]: {
        id: artboardId,
        name: "Front panel",
        kind: "front",
        frame: { x: 0, y: 0, width: options.width, height: options.height },
      },
    },
    roots: [rootId],
    nodes: {
      [rootId]: {
        id: rootId,
        name: "Front panel",
        kind: "surface",
        artboardId,
        frame: { x: 0, y: 0, width: options.width, height: options.height },
        styleOverrides: { fill: "#171b1c" },
      },
    },
    semantics: {},
    styles: {},
    assets: {},
    componentLibrary: { package: "@patchwork/core", version: 1 },
  });
}
